import React, { useCallback, useEffect, useState } from "react";
import {
  fetchDataAlleGebruikers,
  handleSignUp,
  updateToegang,
} from "../../apiCalls/usersApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDoorClosed, faDoorOpen } from "@fortawesome/free-solid-svg-icons";
import SelectGebruikers from "../../Componenten/SelectGebruikers";
import SendEmailButton2 from "../../Componenten/Email/SendEmailButton2";

const desiredColumns = [
  "toegang",
  "inscription",
  "username",
  "first_name",
  "last_name",
  "phone",
  "email",
  "password",
  "id",
  "role",
  "notes",
];

const btnGhost =
  "rounded-lg border border-wa-border bg-wa-panel px-3 py-1.5 text-left text-xs font-medium text-wa-text transition-colors hover:border-wa-accent/60 hover:bg-wa-hover disabled:cursor-not-allowed disabled:opacity-40";

const btnRegistered =
  "cursor-default rounded-lg border border-wa-accent/35 bg-wa-accent/20 px-3 py-1.5 text-xs font-medium text-wa-text";

const Gebruikers = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [role, setRole] = useState(999);
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("Alle gebruikers");

  useEffect(() => {
    fetchDataAlleGebruikers(
      page,
      pageSize,
      setLoading,
      setError,
      setData,
      desiredColumns,
      role,
      type
    );
  }, [page, pageSize, role, type]);

  const signUp = useCallback(
    async (item) => {
      handleSignUp(
        item,
        page,
        pageSize,
        setLoading,
        setError,
        setData,
        desiredColumns,
        role,
        type
      );
    },
    [page, pageSize, role, type]
  );

  const toegangPlatorm = useCallback(
    async (item) => {
      updateToegang(
        item,
        page,
        pageSize,
        setLoading,
        setError,
        setData,
        desiredColumns,
        role,
        type
      );
    },
    [page, pageSize, role, type]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-wa-app text-wa-text">
      <SelectGebruikers
        setData={setData}
        data={data}
        setPage={setPage}
        setPageSize={setPageSize}
        setLoading={setLoading}
        setError={setError}
        role={role}
        type={type}
        desiredColumns={desiredColumns}
        setType={setType}
        setRole={setRole}
        page={page}
        pageSize={pageSize}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <div className="flex flex-1 flex-col px-3 pb-8 pt-2 sm:px-4">
        {loading ? (
          <p className="py-6 text-center text-sm text-wa-muted">Laden…</p>
        ) : null}
        {error ? (
          <p className="rounded-lg border border-red-800/50 bg-red-950/30 px-3 py-2 text-sm text-red-200" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-2 space-y-3 lg:hidden">
          {data.map((row, rowIndex) => {
            const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
            const hasMail = row.email || (row?.username?.includes("@") && row.role !== 999);
            return (
              <article
                key={`${row.id || row.username || rowIndex}-mobile`}
                className="rounded-xl border border-wa-border bg-wa-panel2 p-4 shadow-sm shadow-black/10"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-wa-text">
                      {fullName || row.username || "Gebruiker"}
                    </h3>
                    <p className="mt-0.5 truncate text-sm text-wa-text/70">{row.username}</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-2"
                    onClick={() => toegangPlatorm(row)}
                    title={
                      row?.toegang > 0
                        ? "Heeft toegang tot de portaal"
                        : "Geen toegang tot de portaal"
                    }
                  >
                    {row?.toegang > 0 ? (
                      <FontAwesomeIcon className="h-5 w-5 text-wa-accent" icon={faDoorOpen} />
                    ) : (
                      <FontAwesomeIcon className="h-5 w-5 text-wa-muted" icon={faDoorClosed} />
                    )}
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-wa-text/75 sm:grid-cols-2">
                  <p className="truncate">
                    <span className="font-semibold text-wa-text/80">Telefoon: </span>
                    {row.phone || "—"}
                  </p>
                  <p className="truncate">
                    <span className="font-semibold text-wa-text/80">Email: </span>
                    {row.email || "—"}
                  </p>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={row.inscription === 1 ? btnRegistered : btnGhost}
                    disabled={row.inscription === 1}
                    onClick={() => signUp(row)}
                  >
                    {row.inscription === 0 ? "Inschrijven" : "Ingeschreven"}
                  </button>

                  {hasMail ? (
                    <SendEmailButton2
                      email={{
                        receiver: row.email || row.username,
                        user: row.username,
                        first_name: row.first_name,
                        last_name: row.last_name,
                        name: `${row?.first_name || ""} ${row?.last_name || ""}`.trim(),
                        password: row.password,
                        from_name: row.first_name,
                      }}
                    />
                  ) : (
                    <span className="rounded-md border border-wa-border bg-wa-panel px-2 py-1 font-mono text-[11px] text-wa-muted">
                      {row.password || "—"}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-2 hidden overflow-x-auto rounded-xl border border-wa-border shadow-lg shadow-black/20 lg:block">
          <table className="w-full min-w-[920px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-wa-border bg-wa-panel">
                <th scope="col" className="w-14 whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Toegang
                </th>
                <th scope="col" className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Inschrijving
                </th>
                <th scope="col" className="min-w-[12rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Username
                </th>
                <th scope="col" className="min-w-[10rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Voornaam
                </th>
                <th scope="col" className="min-w-[10rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Achternaam
                </th>
                <th scope="col" className="min-w-[10rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Telefoon
                </th>
                <th scope="col" className="min-w-[14rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Email
                </th>
                <th scope="col" className="min-w-[12rem] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-wa-muted">
                  Actie
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wa-border bg-wa-panel2">
              {data.length > 0 &&
                data.map((row, rowIndex) => (
                  <tr
                    key={`${row.id || row.username || rowIndex}-desktop`}
                    className="transition-colors hover:bg-wa-rowhover"
                  >
                    <td
                      className="w-14 cursor-pointer px-4 py-3 align-middle"
                      onClick={() => toegangPlatorm(row)}
                      title={
                        row?.toegang > 0
                          ? "Heeft toegang tot de portaal"
                          : "Geen toegang tot de portaal"
                      }
                    >
                      {row?.toegang > 0 ? (
                        <FontAwesomeIcon className="h-5 w-5 text-wa-accent" icon={faDoorOpen} />
                      ) : (
                        <FontAwesomeIcon className="h-5 w-5 text-wa-muted" icon={faDoorClosed} />
                      )}
                    </td>

                    <td className="px-4 py-3 align-middle">
                      <button
                        type="button"
                        className={row.inscription === 1 ? btnRegistered : btnGhost}
                        disabled={row.inscription === 1}
                        onClick={() => signUp(row)}
                      >
                        {row.inscription === 0 ? "Inschrijven" : "Ingeschreven"}
                      </button>
                    </td>

                    <td className="max-w-xs px-4 py-3 align-middle text-wa-text">
                      <span className="block truncate" title={row.username != null ? String(row.username) : ""}>
                        {row.username}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-3 align-middle text-wa-text">
                      <span className="block truncate" title={row.first_name != null ? String(row.first_name) : ""}>
                        {row.first_name}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-3 align-middle text-wa-text">
                      <span className="block truncate" title={row.last_name != null ? String(row.last_name) : ""}>
                        {row.last_name}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-3 align-middle text-wa-text">
                      <span className="block truncate" title={row.phone != null ? String(row.phone) : ""}>
                        {row.phone}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-3 align-middle text-wa-text">
                      <span className="block truncate" title={row.email != null ? String(row.email) : ""}>
                        {row.email}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-middle text-wa-text">
                      {row.email || (row?.username?.includes("@") && row.role !== 999) ? (
                        <SendEmailButton2
                          email={{
                            receiver: row.email || row.username,
                            user: row.username,
                            first_name: row.first_name,
                            last_name: row.last_name,
                            name: `${row?.first_name || ""} ${row?.last_name || ""}`.trim(),
                            password: row.password,
                            from_name: row.first_name,
                          }}
                        />
                      ) : (
                        <span className="font-mono text-xs text-wa-muted">{row.password}</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!loading && data.length === 0 ? (
          <p className="py-10 text-center text-sm text-wa-muted">Geen gebruikers gevonden.</p>
        ) : null}
      </div>
    </div>
  );
};

export default Gebruikers;
